import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PresentationTemplate } from '../../../mocks/presentationTemplates';
import { presentationTemplates } from '../../../mocks/presentationTemplates';
import SlideAnnotationCanvas, { AnnotationShape, AnnotationTool, AnnotationToolbar } from './SlideAnnotationCanvas';

interface SlidePreviewModalProps {
  template: PresentationTemplate;
  onClose: () => void;
  onUse: () => void;
  onSwitchTemplate?: (t: PresentationTemplate) => void;
}

// ── Infographic type detection ─────────────────────────────────────────────
function detectModalInfographicType(template: PresentationTemplate): {
  label: string; icon: string; color: string; bg: string;
} {
  const text = `${template.name} ${template.tags.join(' ')} ${template.description}`.toLowerCase();
  if (text.includes('timeline'))
    return { label: 'Timeline',     icon: 'ri-time-line',               color: '#0891b2', bg: '#e0f2fe' };
  if (text.includes('swot'))
    return { label: 'SWOT',         icon: 'ri-focus-3-line',             color: '#7c3aed', bg: '#ede9fe' };
  if (text.includes('dashboard'))
    return { label: 'Dashboard',    icon: 'ri-dashboard-2-line',         color: '#059669', bg: '#d1fae5' };
  if (text.includes('process') || text.includes('flow') || text.includes('cycle'))
    return { label: 'Process Flow', icon: 'ri-git-branch-line',          color: '#d97706', bg: '#fef3c7' };
  if (text.includes('pyramid'))
    return { label: 'Pyramid',      icon: 'ri-bar-chart-grouped-line',   color: '#e11d48', bg: '#ffe4e6' };
  if (text.includes('comparison') || text.includes('compare') || text.includes('vs'))
    return { label: 'Comparison',   icon: 'ri-scales-3-line',            color: '#db2777', bg: '#fce7f3' };
  return   { label: 'Infographic',  icon: 'ri-pie-chart-2-line',         color: '#64748b', bg: '#f1f5f9' };
}

// ── Usage stats (mock) ─────────────────────────────────────────────────────
function getUsageStats(template: PresentationTemplate) {
  const seed = template.id.charCodeAt(0) + template.id.charCodeAt(template.id.length - 1);
  return {
    uses:    Math.floor((seed * 137 + 4200) % 9800 + 1200),
    rating:  ((seed % 10) / 10 + 4.3).toFixed(1),
    exports: Math.floor((seed * 53 + 800) % 3400 + 400),
  };
}

// ── "Used by" brand roles per category ────────────────────────────────────
type BrandRole = { label: string; icon: string; color: string };
function getBrandRoles(category: string): BrandRole[] {
  const map: Record<string, BrandRole[]> = {
    Business:      [{ label: 'Consulting Firms', icon: 'ri-briefcase-4-line', color: '#0891b2' }, { label: 'Fortune 500', icon: 'ri-building-2-line', color: '#059669' }, { label: 'SMBs', icon: 'ri-store-2-line', color: '#d97706' }, { label: 'C-Suite Execs', icon: 'ri-user-star-line', color: '#7c3aed' }],
    Marketing:     [{ label: 'Agencies', icon: 'ri-megaphone-line', color: '#e11d48' }, { label: 'Brand Teams', icon: 'ri-award-line', color: '#db2777' }, { label: 'Growth Hackers', icon: 'ri-rocket-2-line', color: '#d97706' }, { label: 'Ad Studios', icon: 'ri-pencil-ruler-2-line', color: '#0891b2' }],
    Education:     [{ label: 'Universities', icon: 'ri-graduation-cap-line', color: '#059669' }, { label: 'Online Courses', icon: 'ri-computer-line', color: '#0891b2' }, { label: 'K-12 Schools', icon: 'ri-book-open-line', color: '#d97706' }, { label: 'EdTech Startups', icon: 'ri-lightbulb-line', color: '#7c3aed' }],
    Sales:         [{ label: 'Sales Teams', icon: 'ri-line-chart-line', color: '#e11d48' }, { label: 'Account Execs', icon: 'ri-user-3-line', color: '#059669' }, { label: 'SaaS Companies', icon: 'ri-cloud-line', color: '#0891b2' }, { label: 'B2B Agencies', icon: 'ri-exchange-line', color: '#d97706' }],
    Startup:       [{ label: 'Founders', icon: 'ri-rocket-2-line', color: '#e11d48' }, { label: 'VCs & Angels', icon: 'ri-money-dollar-circle-line', color: '#059669' }, { label: 'Accelerators', icon: 'ri-speed-line', color: '#0891b2' }, { label: 'Product Teams', icon: 'ri-apps-line', color: '#7c3aed' }],
    Finance:       [{ label: 'CFOs & Analysts', icon: 'ri-bar-chart-grouped-line', color: '#0891b2' }, { label: 'FinTech Firms', icon: 'ri-bank-line', color: '#059669' }, { label: 'Investment Banks', icon: 'ri-funds-line', color: '#d97706' }, { label: 'Audit Firms', icon: 'ri-file-chart-line', color: '#64748b' }],
    Tech:          [{ label: 'Engineering Teams', icon: 'ri-code-s-slash-line', color: '#7c3aed' }, { label: 'Product Managers', icon: 'ri-roadmap-line', color: '#0891b2' }, { label: 'Dev Agencies', icon: 'ri-terminal-box-line', color: '#059669' }, { label: 'SaaS Founders', icon: 'ri-cloud-line', color: '#d97706' }],
    Medical:       [{ label: 'Hospitals', icon: 'ri-hospital-line', color: '#059669' }, { label: 'Pharma Companies', icon: 'ri-medicine-bottle-line', color: '#0891b2' }, { label: 'Research Labs', icon: 'ri-microscope-line', color: '#7c3aed' }, { label: 'Health Startups', icon: 'ri-heart-pulse-line', color: '#e11d48' }],
    Creative:      [{ label: 'Design Studios', icon: 'ri-palette-line', color: '#db2777' }, { label: 'Freelancers', icon: 'ri-user-star-line', color: '#7c3aed' }, { label: 'Ad Agencies', icon: 'ri-brush-2-line', color: '#d97706' }, { label: 'Portfolio Artists', icon: 'ri-gallery-line', color: '#0891b2' }],
    HR:            [{ label: 'HR Departments', icon: 'ri-group-line', color: '#059669' }, { label: 'Recruiters', icon: 'ri-user-search-line', color: '#0891b2' }, { label: 'People Ops', icon: 'ri-team-line', color: '#d97706' }, { label: 'L&D Teams', icon: 'ri-book-mark-line', color: '#7c3aed' }],
    'Non-Profit':  [{ label: 'NGOs', icon: 'ri-earth-line', color: '#059669' }, { label: 'Grant Writers', icon: 'ri-file-text-line', color: '#0891b2' }, { label: 'Fundraisers', icon: 'ri-hand-heart-line', color: '#e11d48' }, { label: 'Community Orgs', icon: 'ri-community-line', color: '#d97706' }],
    'Real Estate': [{ label: 'Realtors', icon: 'ri-building-line', color: '#d97706' }, { label: 'Property Devs', icon: 'ri-home-gear-line', color: '#059669' }, { label: 'Mortgage Brokers', icon: 'ri-money-dollar-box-line', color: '#0891b2' }, { label: 'Investment Trusts', icon: 'ri-funds-box-line', color: '#7c3aed' }],
  };
  return map[category] ?? [
    { label: 'All Industries', icon: 'ri-global-line', color: '#64748b' },
    { label: 'Agencies', icon: 'ri-award-line', color: '#7c3aed' },
    { label: 'Teams & Orgs', icon: 'ri-team-line', color: '#0891b2' },
    { label: 'Freelancers', icon: 'ri-user-star-line', color: '#d97706' },
  ];
}

// ── Speaker notes generator ────────────────────────────────────────────────
function generateSpeakerNotes(
  slideLabel: string,
  slideIdx: number,
  template: PresentationTemplate,
): string {
  const tag0 = template.tags[0] ?? 'the key topic';
  const tag1 = template.tags[1] ?? 'core insight';
  const cat  = template.category.toLowerCase();

  const notes: Partial<Record<string, string>> = {
    'Title Slide':
      `Greet the audience warmly and introduce yourself — name, role, and why you're presenting "${template.name}" today. This is a ${cat} audience, so open with a hook relevant to ${tag0}.\n\nPause 3–4 seconds after the title appears. Let it breathe. Try opening with: "How many of you have dealt with a challenge around ${tag0}?" to immediately pull them in.`,

    'Agenda':
      `Walk through each agenda item at a steady pace — about 10 seconds per point. Tell the room how long the session runs and whether Q&A is at the end or ongoing.\n\nFor a ${cat} audience, briefly acknowledge any assumed background knowledge. This signals respect for their time and builds credibility from the first minute.`,

    'Overview':
      `This is your elevator pitch for the entire deck. Hit three things in under 30 seconds: what the problem is, what this presentation proposes, and why it matters specifically for ${tag0}.\n\nSpeak slowly here — this slide anchors everything that follows. If the audience remembers only one slide, it should be this one. Consider writing the key phrase on a card to hold in your hand.`,

    'Data & Stats':
      `Don't just read the numbers — tell the story behind them. For each stat, follow the structure: "The number is X… which means Y… and for you that translates to Z."\n\nFor a ${cat} context, always tie data to ROI, risk reduction, or strategic advantage. Pause after each major figure to let it land. Silence here is your friend.`,

    'Key Points':
      `Deliver each bullet as a mini-story using Point → Reason → Example. For ${tag0}, share a short real-world scenario if you have one — it makes abstract points concrete.\n\nDon't read from the slide. Make eye contact and use the bullets as memory prompts. Transition between points with connectors: "Building on that…" or "The second thing worth noting is…"`,

    'Case Study':
      `Set up the case in one sentence: who it was, what the challenge was. Then walk the audience through: the situation, the approach taken, and the measurable results.\n\nFor ${cat} audiences, connect the outcome back to their own context — "The principle here applies directly to your situation when…". Use real numbers wherever possible.`,

    'Process Flow':
      `Walk through each step slowly and pause at any decision points or handoffs. ${tag1} is the step most practitioners skip — spend an extra beat emphasizing it.\n\nUse your hands to gesture left-to-right through the flow. Physical movement reinforces the sequential structure. Offer to pause for questions between major stages if the audience looks complex.`,

    'Timeline':
      `First orient the audience in time: "We started here, we're currently at this point, and this is where we're headed." For ${cat} presentations, tie each milestone to a business outcome or an external event.\n\nIf the timeline extends into the future, be clear about what's confirmed vs. what's aspirational. Honesty about uncertainty builds far more credibility than false precision.`,

    'Comparison':
      `Position this as "different tools for different situations" unless you're making an explicit recommendation. For ${tag0}, clarify which option best fits the audience's specific use case — don't make them guess.\n\nLet the visual do the lifting. Point to specific rows, not the entire table. Focus commentary on the 2–3 dimensions that matter most to this room.`,

    'Quote / Insight':
      `Read the quote at a measured, deliberate pace. Then — pause. Hold silence for 2–3 seconds before commenting. That silence creates weight and emotional resonance that rushing destroys.\n\nBriefly explain who said it and why their perspective on ${tag1} is authoritative. Then bridge it to your argument in a single sentence: "What this means for us is…"`,

    'Team':
      `Introduce each team member with context for why their background is relevant to this ${cat} work — not just their title. Micro-stories are more memorable than org charts.\n\nIf this is a pitch, emphasize track record and complementary skills. If it's an internal presentation, focus on ownership and accountability. Keep each intro to 15–20 seconds max.`,

    'Roadmap':
      `Frame the roadmap as a living document — best current thinking, not a rigid contract. For ${cat} audiences, anticipate the two most common objections: timeline feasibility and resource availability.\n\nHighlight 1–2 highest-priority milestones. Everything else is supporting context. If pressed on timelines, defer to the discovery and iteration process rather than giving fixed dates.`,

    'Call to Action':
      `Be direct and specific. Tell the audience exactly what you want them to do next — schedule a meeting, sign off on a budget, complete a form. Vague CTAs lose momentum the moment people leave the room.\n\nRepeat the CTA twice: once while presenting and once while pointing to the slide text. Remove every possible friction point. If there's a link, have it available to share immediately.`,

    'Thank You':
      `Close with genuine warmth — not a rushed "that's it." Restate the single most important takeaway from "${template.name}" in one crisp sentence.\n\nShare your contact info verbally, not just visually. Invite follow-ups by email if time was short. End with an open invitation: "What questions do you have?" is far stronger than "Any questions?" — which invites silence.`,
  };

  const specific = notes[slideLabel];
  if (specific) return specific;

  return `Slide ${slideIdx + 1} — ${slideLabel}\n\nDeliver this section with confidence and purpose. Connect the content back to "${template.name}" and what the ${cat} audience needs to walk away understanding.\n\nRemember: your slides support your words — they're not your script. Speak naturally, make eye contact, and leave space for the ideas to settle before advancing.`;
}

// ── Similar templates ──────────────────────────────────────────────────────
function getSimilarTemplates(template: PresentationTemplate, count = 6): PresentationTemplate[] {
  const currentType = detectModalInfographicType(template);
  return presentationTemplates
    .filter(t => t.id !== template.id)
    .map(t => {
      let score = 0;
      if (t.category === template.category) score += 3;
      if (detectModalInfographicType(t).label === currentType.label) score += 4;
      score += t.tags.filter(tag => template.tags.includes(tag)).length * 2;
      score += (t.id.charCodeAt(0) % 5) * 0.1;
      return { t, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
    .map(({ t }) => t);
}

// ── Slide definitions ──────────────────────────────────────────────────────
function generateSlides(template: PresentationTemplate) {
  const slideTypes = [
    { label: 'Title Slide',     icon: 'ri-home-3-line',        desc: 'Opening title with headline and subtitle' },
    { label: 'Agenda',          icon: 'ri-list-check-2',       desc: 'Table of contents and session overview' },
    { label: 'Overview',        icon: 'ri-eye-line',           desc: 'High-level summary and key takeaways' },
    { label: 'Data & Stats',    icon: 'ri-bar-chart-box-line', desc: 'Charts, graphs and key metrics' },
    { label: 'Key Points',      icon: 'ri-focus-3-line',       desc: 'Bullet points and main arguments' },
    { label: 'Case Study',      icon: 'ri-briefcase-4-line',   desc: 'Real-world example and evidence' },
    { label: 'Process Flow',    icon: 'ri-flow-chart',         desc: 'Step-by-step workflow diagram' },
    { label: 'Timeline',        icon: 'ri-time-line',          desc: 'Project or event chronology' },
    { label: 'Comparison',      icon: 'ri-scales-3-line',      desc: 'Side-by-side feature comparison' },
    { label: 'Quote / Insight', icon: 'ri-double-quotes-l',    desc: 'Impactful quote from an expert or leader' },
    { label: 'Team',            icon: 'ri-team-line',          desc: 'Team member introductions and bios' },
    { label: 'Roadmap',         icon: 'ri-road-map-line',      desc: 'Future plans and milestones' },
    { label: 'Call to Action',  icon: 'ri-megaphone-line',     desc: 'Final CTA and next steps' },
    { label: 'Thank You',       icon: 'ri-hand-heart-line',    desc: 'Closing slide with contact info' },
  ];
  return Array.from({ length: Math.min(template.slideCount, 14) }, (_, i) => {
    const type = slideTypes[i % slideTypes.length];
    const seqBase = template.id.replace(/[^a-z0-9]/g, '').substring(0, 8);
    return {
      index: i + 1,
      label: type.label,
      icon:  type.icon,
      desc:  type.desc,
      thumbnail: `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%60$%7Btemplate.category%7D%20professional%20presentation%20slide%20$%7Btype.label.toLowerCase%28%29%7D%20$%7Btemplate.tags.join%28%20%29%7D%20modern%20design%20clean%20widescreen%2016:9%20high%20quality%60%20%20%20%20%20%20%20%29%7D&width=960&height=540&seq=slpv-${seqBase}-${i}&orientation=landscape`,
    };
  });
}

// ── Keyboard shortcut constants ────────────────────────────────────────────
const SHORTCUTS = [
  { keys: ['←', '→'], label: 'Previous / Next slide',    icon: 'ri-arrow-left-right-line' },
  { keys: ['Space'],   label: 'Play / Pause slideshow',   icon: 'ri-play-circle-line' },
  { keys: ['Home'],    label: 'Jump to first slide',      icon: 'ri-skip-back-line' },
  { keys: ['End'],     label: 'Jump to last slide',       icon: 'ri-skip-forward-line' },
  { keys: ['F'],       label: 'Toggle fullscreen',        icon: 'ri-fullscreen-line' },
  { keys: ['P'],       label: 'Toggle presenter mode',    icon: 'ri-presentation-line' },
  { keys: ['A'],       label: 'Toggle annotation mode',   icon: 'ri-edit-2-line' },
  { keys: ['N'],       label: 'Toggle speaker notes',     icon: 'ri-file-text-line' },
  { keys: ['E'],       label: 'Export all notes',         icon: 'ri-export-line' },
  { keys: ['D'],       label: 'Download sample PDF',      icon: 'ri-download-cloud-line' },
  { keys: ['Ctrl+Z'],  label: 'Undo last annotation',     icon: 'ri-arrow-go-back-line' },
  { keys: ['?'],       label: 'Show / hide shortcuts',    icon: 'ri-keyboard-line' },
  { keys: ['Esc'],     label: 'Close modal',              icon: 'ri-close-circle-line' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

// ── Speaker Notes Panel ────────────────────────────────────────────────────
type SlideInfo = ReturnType<typeof generateSlides>[number];

function SpeakerNotesPanel({
  currentSlide,
  currentIdx,
  totalSlides,
  note,
  onNoteChange,
  onClose,
  accentColor,
}: {
  currentSlide: SlideInfo;
  currentIdx: number;
  totalSlides: number;
  note: string;
  onNoteChange: (val: string) => void;
  onClose: () => void;
  accentColor: string;
}) {
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when slide changes
  useEffect(() => {
    textareaRef.current?.focus();
  }, [currentIdx]);

  const handleCopy = () => {
    navigator.clipboard.writeText(note).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const wordCount = note.trim().split(/\s+/).filter(Boolean).length;
  const charCount = note.length;

  return (
    <div
      className="flex-shrink-0 flex flex-col border-t border-white/10"
      style={{ height: '172px', backgroundColor: '#0a0f1a' }}
    >
      {/* ── Notes header bar ── */}
      <div
        className="flex-shrink-0 flex items-center gap-2 px-4 py-2 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
      >
        {/* Icon + label */}
        <div
          className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accentColor }}
        >
          <i className="ri-file-text-line text-white text-[9px]"></i>
        </div>
        <span className="text-xs font-bold text-white/70 whitespace-nowrap">Speaker Notes</span>

        {/* Divider */}
        <span className="text-white/20 text-xs">·</span>

        {/* Slide label */}
        <i className={`${currentSlide.icon} text-[10px] text-white/35 flex-shrink-0`}></i>
        <span className="text-[11px] text-white/40 truncate min-w-0">
          {currentIdx + 1}/{totalSlides} — {currentSlide.label}
        </span>

        {/* Spacer */}
        <span className="flex-1" />

        {/* Word / char count */}
        <span className="text-[10px] text-white/25 whitespace-nowrap hidden md:inline">
          {wordCount}w · {charCount}ch
        </span>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/8 hover:bg-white/15 text-white/50 hover:text-white text-[10px] font-medium cursor-pointer transition-colors whitespace-nowrap"
          style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
        >
          <i className={`text-[10px] ${copied ? 'ri-check-line text-emerald-400' : 'ri-clipboard-line'}`}></i>
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>

        {/* Close */}
        <button
          onClick={onClose}
          className="w-5 h-5 flex items-center justify-center rounded-md bg-white/8 hover:bg-white/15 text-white/35 hover:text-white/80 cursor-pointer transition-colors flex-shrink-0"
          title="Close notes (N)"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      </div>

      {/* ── Editable notes textarea ── */}
      <textarea
        ref={textareaRef}
        value={note}
        onChange={e => onNoteChange(e.target.value)}
        className="flex-1 w-full bg-transparent resize-none px-4 py-2.5 focus:outline-none leading-relaxed text-xs placeholder-white/20"
        placeholder="Add your speaker notes for this slide…"
        style={{
          color: 'rgba(255,255,255,0.65)',
          fontFamily: "'Courier New', Courier, monospace",
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.12) transparent',
        }}
      />
    </div>
  );
}

// ── Similar Templates Row ──────────────────────────────────────────────────
function SimilarTemplatesRow({
  similar, onSwitch, accentColor,
}: { similar: PresentationTemplate[]; onSwitch?: (t: PresentationTemplate) => void; accentColor: string }) {
  if (similar.length === 0) return null;
  return (
    <div className="flex-shrink-0 border-t border-slate-100 bg-slate-50/80">
      <div className="flex items-center gap-2 px-5 pt-3 pb-2">
        <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accentColor }}>
          <i className="ri-layout-grid-2-line text-white text-[10px]"></i>
        </div>
        <span className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">Similar Templates</span>
        <span className="text-[11px] text-slate-400">— same category &amp; type</span>
        <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold text-white flex-shrink-0" style={{ backgroundColor: accentColor }}>
          {similar.length} picks
        </span>
      </div>
      <div className="flex gap-2.5 overflow-x-auto px-5 pb-3" style={{ scrollbarWidth: 'none' }}>
        {similar.map(t => {
          const type = detectModalInfographicType(t);
          return (
            <button
              key={t.id}
              onClick={() => onSwitch?.(t)}
              className="group/sim flex-shrink-0 w-44 rounded-xl overflow-hidden border-2 border-slate-200 hover:border-rose-400 bg-white cursor-pointer transition-all duration-200 hover:-translate-y-0.5 text-left"
            >
              <div className="relative h-24 overflow-hidden bg-slate-100">
                <img src={t.thumbnail} alt={t.name} className="w-full h-full object-cover group-hover/sim:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover/sim:opacity-100 transition-opacity duration-200" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/sim:opacity-100 transition-opacity duration-200">
                  <div className="flex items-center gap-1 px-3 py-1 bg-white/90 rounded-full text-xs font-bold text-slate-700">
                    <i className="ri-eye-line text-xs"></i>Preview
                  </div>
                </div>
                <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ backgroundColor: `${type.color}dd` }}>
                  <i className={`${type.icon} text-[9px]`}></i>{type.label}
                </div>
                <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-black/50 backdrop-blur-sm rounded-md">
                  <span className="text-[9px] font-bold text-white">{t.slideCount} slides</span>
                </div>
              </div>
              <div className="px-2.5 py-2">
                <p className="text-xs font-bold text-slate-800 truncate group-hover/sim:text-rose-600 transition-colors leading-tight">{t.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-slate-400 truncate">{t.category}</span>
                  <div className="flex gap-0.5 flex-shrink-0 ml-1">
                    {t.colorPalette.slice(0, 3).map((c, i) => (
                      <div key={i} className="w-2.5 h-2.5 rounded-full border border-white/60" style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Keyboard Shortcuts Panel ───────────────────────────────────────────────
function KeyboardShortcutsPanel({ onClose, accentColor }: { onClose: () => void; accentColor: string }) {
  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative z-10 bg-slate-900 rounded-2xl border border-white/10 shadow-2xl w-[440px] max-w-[90vw] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b border-white/10"
          style={{ background: `linear-gradient(135deg, ${accentColor}22, transparent)` }}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: accentColor }}>
              <i className="ri-keyboard-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-sm font-extrabold text-white leading-none">Keyboard Shortcuts</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Navigate the preview faster</p>
            </div>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white cursor-pointer transition-colors">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
        <div className="p-5 grid grid-cols-1 gap-2">
          {SHORTCUTS.map((s, idx) => (
            <div key={idx} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/5">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${s.icon} text-sm`} style={{ color: accentColor }}></i>
              </div>
              <span className="flex-1 text-xs text-slate-300">{s.label}</span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {s.keys.map((k, ki) => (
                  <span key={ki} className="inline-flex items-center">
                    {ki > 0 && <span className="text-[10px] text-slate-500 mx-0.5">/</span>}
                    <kbd className="px-2 py-0.5 bg-slate-700 border border-slate-600 text-white text-[11px] font-mono font-bold rounded-md">{k}</kbd>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 pb-4">
          <p className="text-center text-[11px] text-slate-500">
            Press <kbd className="px-1.5 py-0.5 bg-slate-700 border border-slate-600 text-white text-[10px] font-mono rounded-md">?</kbd> again to dismiss
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Download Sample Button ─────────────────────────────────────────────────
type DownloadState = 'idle' | 'progress' | 'done';

function DownloadSampleButton({ template, accentColor }: { template: PresentationTemplate; accentColor: string }) {
  const [dlState, setDlState] = useState<DownloadState>('idle');
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startDownload = () => {
    if (dlState !== 'idle') return;
    setDlState('progress');
    setProgress(0);
    let p = 0;
    intervalRef.current = setInterval(() => {
      p += Math.random() * 18 + 8;
      if (p >= 100) {
        p = 100;
        clearInterval(intervalRef.current!);
        setProgress(100);
        setTimeout(() => {
          setDlState('done');
          setTimeout(() => { setDlState('idle'); setProgress(0); }, 2800);
        }, 200);
      } else {
        setProgress(p);
      }
    }, 160);
  };

  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const stages = ['Cover Slide', 'Key Content', 'Call to Action'];

  return (
    <div className="mt-2.5">
      {dlState === 'idle' && (
        <button
          onClick={startDownload}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 text-xs font-bold cursor-pointer transition-all hover:shadow-sm whitespace-nowrap"
          style={{ borderColor: accentColor, color: accentColor, backgroundColor: `${accentColor}10` }}
        >
          <i className="ri-download-cloud-2-line text-sm"></i>Download Sample (3 slides)
        </button>
      )}
      {dlState === 'progress' && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between mb-2.5">
            {stages.map((stage, i) => {
              const active = progress >= (i + 1) / stages.length * 100 * 0.6;
              return (
                <div key={i} className="flex flex-col items-center gap-1 flex-1">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300" style={{ backgroundColor: active ? accentColor : '#e2e8f0', color: active ? 'white' : '#94a3b8' }}>
                    {active ? <i className="ri-check-line text-[10px]"></i> : i + 1}
                  </div>
                  <span className="text-[9px] text-slate-400 text-center leading-tight">{stage}</span>
                </div>
              );
            })}
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden mb-1.5">
            <div className="h-full rounded-full transition-all duration-200" style={{ width: `${progress}%`, backgroundColor: accentColor }} />
          </div>
          <p className="text-[10px] text-center font-bold" style={{ color: accentColor }}>
            Generating PDF sample… {Math.round(progress)}%
          </p>
        </div>
      )}
      {dlState === 'done' && (
        <div className="rounded-xl p-3 flex items-center gap-3 border" style={{ backgroundColor: `${accentColor}0d`, borderColor: `${accentColor}40` }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accentColor }}>
            <i className="ri-check-double-line text-white text-sm"></i>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-extrabold" style={{ color: accentColor }}>PDF Sample Ready!</p>
            <p className="text-[10px] text-slate-500 truncate mt-0.5">{template.name.slice(0, 26)}… (3 slides)</p>
          </div>
          <i className="ri-file-pdf-2-line text-xl flex-shrink-0" style={{ color: accentColor }}></i>
        </div>
      )}
    </div>
  );
}

// ── Used by Similar Brands ─────────────────────────────────────────────────
function UsedBySimilarBrands({ category, accentColor }: { category: string; accentColor: string }) {
  const roles = getBrandRoles(category);
  return (
    <div className="px-4 py-3 border-t border-slate-100">
      <div className="flex items-center gap-1.5 mb-2.5">
        <i className="ri-group-2-line text-xs flex-shrink-0" style={{ color: accentColor }}></i>
        <p className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Used by similar brands</p>
      </div>
      <div className="flex flex-col gap-1.5">
        {roles.map((role, i) => (
          <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-slate-100 bg-slate-50/60">
            <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${role.color}20` }}>
              <i className={`${role.icon} text-[10px]`} style={{ color: role.color }}></i>
            </div>
            <span className="text-xs text-slate-700 font-medium">{role.label}</span>
            <div className="ml-auto flex-shrink-0">
              <div className="h-1 w-12 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${65 + (i * 7 + role.label.length * 3) % 32}%`, backgroundColor: role.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-slate-400 mt-2 text-center">Based on 2,400+ similar template uses</p>
    </div>
  );
}

// ── Export Notes Modal ─────────────────────────────────────────────────────
function ExportNotesModal({
  template,
  slides,
  speakerNotes,
  onClose,
  accentColor,
}: {
  template: PresentationTemplate;
  slides: ReturnType<typeof generateSlides>;
  speakerNotes: string[];
  onClose: () => void;
  accentColor: string;
}) {
  const [copiedAll, setCopiedAll] = useState(false);
  const [printReady, setPrintReady] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const slidesWithNotes = slides.filter((_, i) => speakerNotes[i]?.trim());
  const totalWords = speakerNotes.reduce(
    (acc, n) => acc + n.trim().split(/\s+/).filter(Boolean).length,
    0,
  );
  const totalChars = speakerNotes.reduce((acc, n) => acc + n.length, 0);

  const buildPlainText = () =>
    slides
      .map(
        (s, i) =>
          `═══════════════════════════════════\nSlide ${i + 1}: ${s.label}\n═══════════════════════════════════\n${speakerNotes[i]?.trim() || '(No notes added for this slide)'}`,
      )
      .join('\n\n') +
    `\n\n─────────────────────────────────────\nDocument: "${template.name}" · ${template.category}\nGenerated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;

  const handleCopyAll = () => {
    navigator.clipboard.writeText(buildPlainText()).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    });
  };

  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = 'export-notes-print-style';
    style.innerHTML = `
      @media print {
        body > * { display: none !important; }
        #export-notes-print-area {
          display: block !important;
          position: static !important;
          background: white !important;
          color: black !important;
          font-family: Georgia, serif !important;
          padding: 2cm !important;
        }
        .print-hide { display: none !important; }
        .print-slide-block {
          page-break-inside: avoid;
          margin-bottom: 2em;
          border-bottom: 1px solid #ccc;
          padding-bottom: 1.5em;
        }
      }
    `;
    document.head.appendChild(style);
    setPrintReady(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.getElementById('export-notes-print-style')?.remove();
        setPrintReady(false);
      }, 800);
    }, 120);
  };

  // Close on Esc key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const infoType = detectModalInfographicType(template);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-[780px] max-w-[95vw] h-[88vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Print area wrapper (hidden visually, shown when printing) */}
        <div id="export-notes-print-area" style={{ display: 'none' }}>
          <div style={{ marginBottom: '1.5em' }}>
            <h1 style={{ fontSize: '1.4em', fontWeight: 'bold' }}>{template.name} — Speaker Notes</h1>
            <p style={{ fontSize: '0.85em', color: '#666', marginTop: '0.3em' }}>
              {template.category} · {slides.length} slides · {totalWords} words · Generated {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          {slides.map((s, i) => (
            <div key={i} className="print-slide-block">
              <div style={{ fontSize: '0.95em', fontWeight: 'bold', marginBottom: '0.4em', color: '#111' }}>
                Slide {i + 1}: {s.label}
              </div>
              <p style={{ fontSize: '0.9em', color: speakerNotes[i]?.trim() ? '#111' : '#aaa', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {speakerNotes[i]?.trim() || 'No notes added for this slide.'}
              </p>
            </div>
          ))}
        </div>

        {/* ── Header ── */}
        <div
          className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-100"
          style={{ background: `linear-gradient(135deg, ${accentColor}15, white 70%)` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: accentColor }}
            >
              <i className="ri-file-text-2-line text-white text-base"></i>
            </div>
            <div className="min-w-0">
              <p className="font-extrabold text-slate-900 text-sm leading-tight">Export Speaker Notes</p>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate">
                {template.name} · {template.category}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Stats pills */}
            <div className="hidden sm:flex items-center gap-1.5 mr-1">
              <span
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
                style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
              >
                <i className="ri-slides-line text-[10px]"></i>
                {slides.length} slides
              </span>
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                <i className="ri-text ri-text text-[10px]"></i>
                {totalWords} words
              </span>
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700">
                <i className="ri-file-edit-line text-[10px]"></i>
                {slidesWithNotes.length} noted
              </span>
            </div>

            <button
              onClick={handleCopyAll}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
              style={
                copiedAll
                  ? { backgroundColor: '#059669', color: 'white' }
                  : { backgroundColor: `${accentColor}15`, color: accentColor, border: `1.5px solid ${accentColor}40` }
              }
            >
              <i className={`text-sm ${copiedAll ? 'ri-check-double-line' : 'ri-clipboard-line'}`}></i>
              {copiedAll ? 'Copied!' : 'Copy All'}
            </button>

            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap text-white"
              style={{ backgroundColor: accentColor }}
            >
              <i className="ri-printer-line text-sm"></i>
              {printReady ? 'Opening…' : 'Print'}
            </button>

            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        {/* ── Summary banner ── */}
        <div className="flex-shrink-0 flex items-center gap-6 px-6 py-2.5 bg-slate-50 border-b border-slate-100">
          {[
            { icon: 'ri-slides-line',     label: 'Total Slides',   val: slides.length.toString() },
            { icon: 'ri-file-edit-line',  label: 'Slides w/ Notes', val: `${slidesWithNotes.length} / ${slides.length}` },
            { icon: 'ri-text',            label: 'Total Words',    val: totalWords.toLocaleString() },
            { icon: 'ri-font-size',       label: 'Total Chars',    val: totalChars.toLocaleString() },
          ].map(item => (
            <div key={item.label} className="flex items-center gap-1.5">
              <i className={`${item.icon} text-xs`} style={{ color: accentColor }}></i>
              <span className="text-[10px] text-slate-500">{item.label}</span>
              <span className="text-xs font-extrabold text-slate-800 ml-0.5">{item.val}</span>
            </div>
          ))}
          <div className="ml-auto hidden sm:flex items-center gap-1 text-[10px] text-slate-400">
            <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 text-[10px] font-mono rounded text-slate-500">Esc</kbd>
            <span>to close</span>
          </div>
        </div>

        {/* ── Document content ── */}
        <div
          ref={printRef}
          className="flex-1 overflow-y-auto"
          style={{ scrollbarWidth: 'thin', scrollbarColor: `${accentColor}40 transparent` }}
        >
          {/* Document header */}
          <div className="px-8 pt-7 pb-5 border-b border-slate-100">
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ backgroundColor: infoType.color }}
              >
                <i className={`${infoType.icon} text-white text-xl`}></i>
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-extrabold text-slate-900 leading-tight">{template.name}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {template.category} presentation · {infoType.label} format · {slides.length} slides
                </p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Speaker notes compiled on {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                {template.colorPalette.map((c, i) => (
                  <div key={i} className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>

          {/* Slides notes */}
          <div className="px-8 py-5 space-y-0">
            {slides.map((slide, i) => {
              const note = speakerNotes[i] ?? '';
              const hasNote = note.trim().length > 0;
              const wc = note.trim().split(/\s+/).filter(Boolean).length;
              const isLast = i === slides.length - 1;

              return (
                <div
                  key={i}
                  className={`relative pb-6 ${!isLast ? 'border-b border-dashed border-slate-200' : ''} pt-5`}
                >
                  {/* Slide header row */}
                  <div className="flex items-center gap-3 mb-3">
                    {/* Number badge */}
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-extrabold text-white"
                      style={{ backgroundColor: hasNote ? accentColor : '#cbd5e1' }}
                    >
                      {i + 1}
                    </div>
                    {/* Icon */}
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: hasNote ? `${accentColor}15` : '#f1f5f9' }}
                    >
                      <i
                        className={`${slide.icon} text-sm`}
                        style={{ color: hasNote ? accentColor : '#94a3b8' }}
                      ></i>
                    </div>
                    {/* Label */}
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-extrabold leading-tight ${hasNote ? 'text-slate-900' : 'text-slate-400'}`}>
                        {slide.label}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1.5 truncate">{slide.desc}</p>
                    </div>
                    {/* Word count / empty badge */}
                    {hasNote ? (
                      <span
                        className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                      >
                        {wc}w
                      </span>
                    ) : (
                      <span className="flex-shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 italic">
                        no notes
                      </span>
                    )}
                  </div>

                  {/* Note body */}
                  {hasNote ? (
                    <div
                      className="ml-[4.5rem] rounded-xl px-4 py-3.5 border leading-relaxed text-sm text-slate-700 whitespace-pre-wrap"
                      style={{
                        backgroundColor: `${accentColor}06`,
                        borderColor: `${accentColor}20`,
                        fontFamily: "'Georgia', serif",
                        borderLeft: `3px solid ${accentColor}`,
                      }}
                    >
                      {note}
                    </div>
                  ) : (
                    <div className="ml-[4.5rem] rounded-xl px-4 py-3 border border-dashed border-slate-200 bg-slate-50/60">
                      <p className="text-xs text-slate-400 italic">No speaker notes added for this slide.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Document footer */}
          <div className="mx-8 mb-8 mt-2 rounded-xl px-5 py-4 flex items-center justify-between border border-slate-100 bg-slate-50/70">
            <div className="flex items-center gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: accentColor }}
              >
                <i className="ri-file-text-2-line text-white text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-extrabold text-slate-700">{template.name}</p>
                <p className="text-[10px] text-slate-400">{template.category} · {slides.length} slides · {totalWords} words</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyAll}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap"
                style={
                  copiedAll
                    ? { backgroundColor: '#059669', color: 'white' }
                    : { border: `1.5px solid ${accentColor}50`, color: accentColor }
                }
              >
                <i className={`text-xs ${copiedAll ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
                {copiedAll ? 'Copied!' : 'Copy All'}
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: accentColor }}
              >
                <i className="ri-printer-line text-xs"></i>Print
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Presenter Mode View ────────────────────────────────────────────────────
function PresenterModeView({
  slides,
  currentIdx,
  speakerNotes,
  onNoteChange,
  onClose,
  onPrev,
  onNext,
  goTo,
  isPlaying,
  onTogglePlay,
  accentColor,
  template,
}: {
  slides: ReturnType<typeof generateSlides>;
  currentIdx: number;
  speakerNotes: string[];
  onNoteChange: (idx: number, val: string) => void;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  goTo: (idx: number) => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  accentColor: string;
  template: PresentationTemplate;
}) {
  const [clock, setClock] = useState(() => new Date());
  const [elapsed, setElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(true);
  const [noteCopied, setNoteCopied] = useState(false);
  const elapsedRef = useRef(elapsed);
  elapsedRef.current = elapsed;

  // Live clock
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Presentation timer
  useEffect(() => {
    if (!timerRunning) return;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [timerRunning]);

  // Keyboard handler inside presenter mode
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA') return;
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowLeft')  { onPrev(); return; }
      if (e.key === 'ArrowRight') { onNext(); return; }
      if (e.key === ' ') { e.preventDefault(); onTogglePlay(); return; }
      if (e.key === 'Home') { goTo(0); return; }
      if (e.key === 'End')  { goTo(slides.length - 1); return; }
      if (e.key === 'p' || e.key === 'P') { onClose(); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, onPrev, onNext, onTogglePlay, goTo, slides.length]);

  const current  = slides[currentIdx];
  const nextSlide = slides[currentIdx + 1] ?? null;
  const note = speakerNotes[currentIdx] ?? '';
  const wordCount = note.trim().split(/\s+/).filter(Boolean).length;

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const handleCopyNote = () => {
    navigator.clipboard.writeText(note).then(() => {
      setNoteCopied(true);
      setTimeout(() => setNoteCopied(false), 2000);
    });
  };

  const infoType = detectModalInfographicType(template);

  return (
    <div className="fixed inset-0 z-[70] flex flex-col" style={{ backgroundColor: '#08101f' }}>

      {/* ── Top bar ── */}
      <div
        className="flex-shrink-0 flex items-center gap-4 px-6 py-3 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.08)', backgroundColor: '#0d1526' }}
      >
        {/* Left: mode label */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: accentColor }}
          >
            <i className="ri-presentation-line text-white text-sm"></i>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-extrabold text-white/90 leading-none">Presenter Mode</p>
            <p className="text-[10px] text-white/35 mt-0.5 truncate">{template.name}</p>
          </div>
        </div>

        {/* Center: slide progress */}
        <div className="flex-1 flex items-center gap-3 justify-center">
          <span className="text-xs text-white/40 whitespace-nowrap">
            Slide <span className="font-extrabold text-white/80">{currentIdx + 1}</span> of <span className="text-white/60">{slides.length}</span>
          </span>
          <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${((currentIdx + 1) / slides.length) * 100}%`, backgroundColor: accentColor }}
            />
          </div>
          <span className="text-[10px] font-mono text-white/40 whitespace-nowrap">
            {Math.round(((currentIdx + 1) / slides.length) * 100)}%
          </span>
        </div>

        {/* Right: clock + timer + exit */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Elapsed timer */}
          <button
            onClick={() => setTimerRunning(r => !r)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg cursor-pointer transition-colors group"
            style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
            title={timerRunning ? 'Pause timer' : 'Resume timer'}
          >
            <i
              className={`text-[10px] ${timerRunning ? 'ri-timer-line' : 'ri-timer-flash-line'}`}
              style={{ color: accentColor }}
            ></i>
            <span className="font-mono text-xs font-bold" style={{ color: accentColor }}>
              {formatElapsed(elapsed)}
            </span>
          </button>

          {/* Live clock */}
          <div
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg"
            style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
          >
            <i className="ri-time-line text-[10px] text-white/30"></i>
            <span className="font-mono text-xs text-white/50">
              {clock.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {/* Exit */}
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white/60 hover:text-white hover:bg-white/10 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className="ri-close-line text-sm"></i>
            <span>Exit</span>
            <kbd className="px-1.5 py-0.5 bg-white/10 text-[9px] font-mono rounded border border-white/15 ml-0.5">Esc</kbd>
          </button>
        </div>
      </div>

      {/* ── Main area ── */}
      <div className="flex-1 flex min-h-0">

        {/* ── Left: slide viewer ── */}
        <div className="flex-1 flex flex-col min-w-0 relative">

          {/* Slide display */}
          <div className="flex-1 flex items-center justify-center p-8 min-h-0 relative">
            <div className="relative w-full max-h-full" style={{ aspectRatio: '16/9' }}>
              <img
                key={currentIdx}
                src={current.thumbnail}
                alt={current.label}
                className="w-full h-full object-cover rounded-2xl"
                style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 24px 64px rgba(0,0,0,0.7)` }}
              />
              {/* Slide badge */}
              <div
                className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs font-bold"
                style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', border: `1px solid ${infoType.color}60` }}
              >
                <div
                  className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: accentColor }}
                >
                  <i className={`${current.icon} text-white text-[9px]`}></i>
                </div>
                {current.label}
              </div>
              {/* Annotation: type */}
              <div
                className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold text-white"
                style={{ backgroundColor: `${accentColor}cc`, backdropFilter: 'blur(6px)' }}
              >
                <i className={`${infoType.icon} text-[10px]`}></i>
                {infoType.label}
              </div>
            </div>

            {/* Nav arrows */}
            <button
              onClick={onPrev}
              disabled={currentIdx === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white transition-all cursor-pointer disabled:opacity-0 disabled:pointer-events-none"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <i className="ri-arrow-left-s-line text-2xl"></i>
            </button>
            <button
              onClick={onNext}
              disabled={currentIdx === slides.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center text-white transition-all cursor-pointer disabled:opacity-0 disabled:pointer-events-none"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}
            >
              <i className="ri-arrow-right-s-line text-2xl"></i>
            </button>
          </div>

          {/* Bottom playback bar */}
          <div
            className="flex-shrink-0 flex items-center gap-3 px-8 py-3 border-t border-white/10 bg-slate-800/50"
            style={{ borderColor: 'rgba(255,255,255,0.07)', backgroundColor: '#0d1526' }}
          >
            <button onClick={() => goTo(0)} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white cursor-pointer transition-colors">
              <i className="ri-skip-back-line text-sm"></i>
            </button>
            <button onClick={onPrev} disabled={currentIdx === 0} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-20 cursor-pointer transition-colors">
              <i className="ri-skip-left-line text-sm"></i>
            </button>
            <button
              onClick={onTogglePlay}
              className="w-9 h-9 rounded-full flex items-center justify-center text-white cursor-pointer transition-colors"
              style={{ backgroundColor: accentColor }}
            >
              <i className={`text-base ${isPlaying ? 'ri-pause-line' : 'ri-play-line'}`}></i>
            </button>
            <button onClick={onNext} disabled={currentIdx === slides.length - 1} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white cursor-pointer transition-colors">
              <i className="ri-skip-right-line text-sm"></i>
            </button>
            <button onClick={() => goTo(slides.length - 1)} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white cursor-pointer transition-colors">
              <i className="ri-skip-forward-line text-sm"></i>
            </button>
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden mx-2" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-200"
                style={{ width: `${((currentIdx + 1) / slides.length) * 100}%`, backgroundColor: accentColor }}
              />
            </div>
            <span className="text-white/35 text-xs font-mono whitespace-nowrap">{currentIdx + 1} / {slides.length}</span>
            <div className="hidden md:flex items-center gap-3 text-white/25 text-[10px] ml-2">
              <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10 font-mono text-[9px]">←</kbd> <kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10 font-mono text-[9px]">→</kbd> navigate</span>
              <span><kbd className="px-1.5 py-0.5 bg-white/10 rounded border border-white/10 font-mono text-[9px]">P</kbd> exit</span>
            </div>
          </div>
        </div>

        {/* ── Right: notes + next slide ── */}
        <div
          className="w-[248px] flex-shrink-0 flex flex-col border-l"
          style={{ borderColor: 'rgba(255,255,255,0.07)', backgroundColor: '#0d1526' }}
        >

          {/* Current slide header */}
          <div
            className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b"
            style={{ borderColor: 'rgba(255,255,255,0.07)', background: `linear-gradient(135deg, ${accentColor}18, transparent)` }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${accentColor}30`, border: `1px solid ${accentColor}50` }}
            >
              <i className={`${current.icon} text-sm`} style={{ color: accentColor }}></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-extrabold text-white/90 leading-none truncate">{current.label}</p>
              <p className="text-[10px] text-white/35 mt-0.5 truncate">{current.desc}</p>
            </div>
            <span
              className="text-[9px] font-extrabold px-2 py-0.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: `${accentColor}25`, color: accentColor }}
            >
              {currentIdx + 1}/{slides.length}
            </span>
          </div>

          {/* Notes area label */}
          <div
            className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-center gap-1.5">
              <i className="ri-file-text-line text-[11px] text-white/40"></i>
              <span className="text-[10px] font-bold text-white/45 uppercase tracking-wider">Speaker Notes</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-white/25">{wordCount}w</span>
              <button
                onClick={handleCopyNote}
                className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium cursor-pointer transition-colors"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)', color: noteCopied ? '#34d399' : 'rgba(255,255,255,0.4)' }}
              >
                <i className={`text-[10px] ${noteCopied ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
                {noteCopied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Editable notes textarea — takes all remaining space above next-up */}
          <textarea
            value={note}
            onChange={e => onNoteChange(currentIdx, e.target.value)}
            className="flex-1 w-full bg-transparent resize-none px-4 py-3 focus:outline-none text-sm leading-relaxed placeholder-white/15"
            placeholder="No speaker notes for this slide. Start typing…"
            style={{
              color: 'rgba(255,255,255,0.72)',
              fontFamily: "'Georgia', serif",
              minHeight: 0,
              scrollbarWidth: 'thin',
              scrollbarColor: `${accentColor}30 transparent`,
            }}
          />

          {/* ── Next Up ── */}
          {nextSlide && (
            <div
              className="flex-shrink-0 border-t"
              style={{ borderColor: 'rgba(255,255,255,0.07)' }}
            >
              <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
                <i className="ri-arrow-right-circle-line text-[10px] text-white/30"></i>
                <span className="text-[10px] font-bold text-white/35 uppercase tracking-wider">Up Next</span>
              </div>
              <div className="px-4 pb-3">
                <button
                  onClick={() => goTo(currentIdx + 1)}
                  className="w-full flex items-start gap-3 p-2.5 rounded-xl cursor-pointer transition-all group"
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
                >
                  <div className="w-20 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-800">
                    <img
                      src={nextSlide.thumbnail}
                      alt={nextSlide.label}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-xs font-bold text-white/75 group-hover:text-white transition-colors leading-tight truncate">
                      {nextSlide.label}
                    </p>
                    <p className="text-[10px] text-white/35 mt-1 leading-tight line-clamp-2">{nextSlide.desc}</p>
                    <div className="flex items-center gap-1 mt-1.5">
                      <i className={`${nextSlide.icon} text-[9px]`} style={{ color: accentColor }}></i>
                      <span className="text-[9px] text-white/25">Slide {currentIdx + 2}</span>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Keyboard shortcut constants ────────────────────────────────────────────
const SHORTCUTS2 = [
  { keys: ['←', '→'], label: 'Previous / Next slide',    icon: 'ri-arrow-left-right-line' },
  { keys: ['Space'],   label: 'Play / Pause slideshow',   icon: 'ri-play-circle-line' },
  { keys: ['Home'],    label: 'Jump to first slide',      icon: 'ri-skip-back-line' },
  { keys: ['End'],     label: 'Jump to last slide',       icon: 'ri-skip-forward-line' },
  { keys: ['F'],       label: 'Toggle fullscreen',        icon: 'ri-fullscreen-line' },
  { keys: ['P'],       label: 'Toggle presenter mode',    icon: 'ri-presentation-line' },
  { keys: ['A'],       label: 'Toggle annotation mode',   icon: 'ri-edit-2-line' },
  { keys: ['N'],       label: 'Toggle speaker notes',     icon: 'ri-file-text-line' },
  { keys: ['E'],       label: 'Export all notes',         icon: 'ri-export-line' },
  { keys: ['D'],       label: 'Download sample PDF',      icon: 'ri-download-cloud-line' },
  { keys: ['Ctrl+Z'],  label: 'Undo last annotation',     icon: 'ri-arrow-go-back-line' },
  { keys: ['?'],       label: 'Show / hide shortcuts',    icon: 'ri-keyboard-line' },
  { keys: ['Esc'],     label: 'Close modal',              icon: 'ri-close-circle-line' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Modal
// ─────────────────────────────────────────────────────────────────────────────
export default function SlidePreviewModal({ template, onClose, onUse, onSwitchTemplate }: SlidePreviewModalProps) {
  const slides = useMemo(() => generateSlides(template), [template.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [currentIdx,        setCurrentIdx]       = useState(0);
  const [loadedSlides,      setLoadedSlides]     = useState<Set<number>>(new Set([0]));
  const [isPlaying,         setIsPlaying]        = useState(false);
  const [fullscreen,        setFullscreen]       = useState(false);
  const [showShortcuts,     setShowShortcuts]    = useState(false);
  const [showNotes,         setShowNotes]        = useState(false);
  const [showExportNotes,   setShowExportNotes]  = useState(false);
  const [showPresenterMode, setShowPresenterMode]= useState(false);

  // ── Annotation states ──────────────────────────────────────────────────
  const [showAnnotations,    setShowAnnotations]    = useState(false);
  const [annotationTool,     setAnnotationTool]     = useState<AnnotationTool>('pen');
  const [annotationColor,    setAnnotationColor]    = useState('#f43f5e');
  const [annotationLineWidth,setAnnotationLineWidth]= useState(4);
  const [annotationsBySlide, setAnnotationsBySlide] = useState<Record<number, AnnotationShape[]>>({});

  // ── Transition states ──────────────────────────────────────────────────
  const [transitionType, setTransitionType] = useState<'none' | 'fade' | 'slide' | 'zoom'>('fade');
  const [transitionKey,  setTransitionKey]  = useState(0);
  const [transitionDir,  setTransitionDir]  = useState<'fwd' | 'bwd'>('fwd');

  // Inject CSS keyframes for slide transitions once
  useEffect(() => {
    const id = 'slide-preview-transition-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes slPrevEnterFade {
        from { opacity: 0; }
        to   { opacity: 1; }
      }
      @keyframes slPrevEnterSlideFwd {
        from { opacity: 0; transform: translateX(52px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes slPrevEnterSlideBwd {
        from { opacity: 0; transform: translateX(-52px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @keyframes slPrevEnterZoom {
        from { opacity: 0; transform: scale(0.91); }
        to   { opacity: 1; transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  // Per-slide notes — initialized once with generated content
  const [speakerNotes, setSpeakerNotes] = useState<string[]>(() =>
    generateSlides(template).map((s, i) => generateSpeakerNotes(s.label, i, template))
  );

  // Reset everything when the template changes (via onSwitchTemplate)
  useEffect(() => {
    setCurrentIdx(0);
    setLoadedSlides(new Set([0]));
    setIsPlaying(false);
    setAnnotationsBySlide({});
    setSpeakerNotes(generateSlides(template).map((s, i) => generateSpeakerNotes(s.label, i, template)));
  }, [template.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const infoType = detectModalInfographicType(template);
  const stats    = getUsageStats(template);
  const similar  = getSimilarTemplates(template);

  const goTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, slides.length - 1));
    if (clamped === currentIdx) return;
    setTransitionDir(clamped > currentIdx ? 'fwd' : 'bwd');
    setTransitionKey(k => k + 1);
    setCurrentIdx(clamped);
    setLoadedSlides(prev => new Set([...prev, clamped]));
  }, [currentIdx, slides.length]);

  const prev = useCallback(() => goTo(currentIdx - 1), [currentIdx, goTo]);
  const next = useCallback(() => goTo(currentIdx + 1), [currentIdx, goTo]);

  // Annotation helpers
  const undoAnnotation = useCallback(() => {
    setAnnotationsBySlide(prev => {
      const cur = [...(prev[currentIdx] ?? [])];
      cur.pop();
      return { ...prev, [currentIdx]: cur };
    });
  }, [currentIdx]);

  const clearAnnotations = useCallback(() => {
    setAnnotationsBySlide(prev => ({ ...prev, [currentIdx]: [] }));
  }, [currentIdx]);

  // Slide animation style
  const slideAnimStyle: React.CSSProperties = transitionType === 'none' ? {} : {
    animation: `${
      transitionType === 'slide'
        ? (transitionDir === 'fwd' ? 'slPrevEnterSlideFwd' : 'slPrevEnterSlideBwd')
        : transitionType === 'zoom'
        ? 'slPrevEnterZoom'
        : 'slPrevEnterFade'
    } 280ms cubic-bezier(0.22, 1, 0.36, 1) both`,
  };

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      // Ctrl+Z — undo annotation
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (showAnnotations) { e.preventDefault(); undoAnnotation(); }
        return;
      }

      if (e.key === 'Escape')             { if (showShortcuts) { setShowShortcuts(false); } else if (showExportNotes) { setShowExportNotes(false); } else if (showAnnotations) { setShowAnnotations(false); } else { onClose(); } return; }
      if (e.key === 'ArrowLeft')          { prev(); return; }
      if (e.key === 'ArrowRight')         { next(); return; }
      if (e.key === ' ')                  { e.preventDefault(); setIsPlaying(p => !p); return; }
      if (e.key === 'f' || e.key === 'F') { setFullscreen(f => !f); return; }
      if (e.key === 'p' || e.key === 'P') { setShowPresenterMode(m => !m); return; }
      if (e.key === 'a' || e.key === 'A') { setShowAnnotations(a => !a); return; }
      if (e.key === 'n' || e.key === 'N') { setShowNotes(n => !n); return; }
      if (e.key === 'e' || e.key === 'E') { setShowExportNotes(n => !n); return; }
      if (e.key === '?')                  { setShowShortcuts(s => !s); return; }
      if (e.key === 'Home')               { goTo(0); return; }
      if (e.key === 'End')                { goTo(slides.length - 1); return; }
      if (e.key === ' ')                  { e.preventDefault(); setIsPlaying(p => !p); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose, goTo, slides.length, showShortcuts, showExportNotes, showAnnotations, undoAnnotation]);

  // Slideshow auto-play
  useEffect(() => {
    if (!isPlaying) return;
    const t = setInterval(() => {
      setCurrentIdx(i => {
        const n = i + 1;
        if (n >= slides.length) { setIsPlaying(false); return i; }
        setLoadedSlides(prev => new Set([...prev, n]));
        return n;
      });
    }, 3000);
    return () => clearInterval(t);
  }, [isPlaying, slides.length]);

  const badgeColors: Record<string, string> = {
    Popular: 'bg-amber-100 text-amber-700',
    New:     'bg-emerald-100 text-emerald-700',
    Trending:'bg-rose-100 text-rose-700',
  };

  const current = slides[currentIdx];

  const updateNote = useCallback((idx: number, val: string) => {
    setSpeakerNotes(prev => { const next = [...prev]; next[idx] = val; return next; });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md">

      {/* ── Presenter Mode overlay ── */}
      {showPresenterMode && (
        <PresenterModeView
          slides={slides}
          currentIdx={currentIdx}
          speakerNotes={speakerNotes}
          onNoteChange={updateNote}
          onClose={() => setShowPresenterMode(false)}
          onPrev={prev}
          onNext={next}
          goTo={goTo}
          isPlaying={isPlaying}
          onTogglePlay={() => setIsPlaying(p => !p)}
          accentColor={infoType.color}
          template={template}
        />
      )}

      <div className={`bg-white flex flex-col transition-all duration-300 ${
        fullscreen ? 'w-full h-full rounded-none' : 'w-[1100px] max-w-[96vw] h-[92vh] rounded-2xl'
      } overflow-hidden shadow-2xl`}>

        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-fuchsia-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-slideshow-line text-white text-sm"></i>
            </div>
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900 text-sm truncate">{template.name}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-slate-400">{template.category}</span>
                {template.badge && (
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${badgeColors[template.badge]}`}>{template.badge}</span>
                )}
                <span className="text-xs text-slate-400">{template.slideCount} slides</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: infoType.color }}>
                  <i className={`${infoType.icon} text-[10px]`}></i>{infoType.label}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="hidden sm:flex gap-1">
              {template.colorPalette.map((c, i) => (
                <div key={i} className="w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: c }} />
              ))}
            </div>
            <button
              onClick={() => setShowExportNotes(true)}
              title="Export all notes (E)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap hover:bg-slate-100 text-slate-500"
            >
              <i className="ri-export-line text-sm"></i>
              <span className="hidden sm:inline">Export Notes</span>
            </button>
            <button
              onClick={() => setShowPresenterMode(true)}
              title="Presenter mode (P)"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap hover:bg-slate-900 hover:text-white text-slate-600"
            >
              <i className="ri-presentation-line text-sm"></i>
              <span className="hidden sm:inline">Present</span>
            </button>
            <button
              onClick={() => setShowNotes(n => !n)}
              title="Speaker notes (N)"
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer whitespace-nowrap ${
                showNotes ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-500'
              }`}
            >
              <i className="ri-file-text-line text-sm"></i>
              <span className="hidden sm:inline">Notes</span>
            </button>
            <button
              onClick={() => setShowShortcuts(s => !s)}
              title="Keyboard shortcuts (?)"
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                showShortcuts ? 'bg-slate-900 text-white' : 'hover:bg-slate-100 text-slate-500'
              }`}
            >
              <i className="ri-keyboard-line text-sm"></i>
            </button>
            <button onClick={() => setFullscreen(f => !f)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer">
              <i className={`text-base ${fullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}`}></i>
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer">
              <i className="ri-close-line text-base"></i>
            </button>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="flex flex-1 min-h-0">

          {/* Slide strip */}
          <div className="w-[160px] flex-shrink-0 border-r border-slate-100 overflow-y-auto bg-slate-50 flex flex-col gap-1.5 p-2" style={{ scrollbarWidth: 'thin' }}>
            {slides.map((slide, idx) => (
              <button
                key={idx}
                onClick={() => goTo(idx)}
                className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                  idx === currentIdx ? 'border-rose-500 ring-2 ring-rose-200' : 'border-transparent hover:border-rose-200'
                }`}
              >
                <div className="aspect-video relative bg-slate-200">
                  {loadedSlides.has(idx) ? (
                    <img src={slide.thumbnail} alt={slide.label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                      <i className={`${slide.icon} text-slate-300 text-lg`}></i>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover/sim:opacity-100 transition-opacity duration-200" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/sim:opacity-100 transition-opacity duration-200">
                    <div className="flex items-center gap-1 px-3 py-1 bg-white/90 rounded-full text-xs font-bold text-slate-700">
                      <i className="ri-eye-line text-xs"></i>Preview
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-5 bg-gradient-to-t from-black/50 to-transparent flex items-end pb-0.5 px-1">
                    <span className="text-white text-[9px] font-bold truncate">{slide.label}</span>
                  </div>
                  {/* Notes indicator dot */}
                  {speakerNotes[idx] && (
                    <div className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ backgroundColor: infoType.color }} title="Has notes" />
                  )}
                  {/* Annotation indicator dot */}
                  {(annotationsBySlide[idx]?.length ?? 0) > 0 && (
                    <div
                      className="absolute top-1 right-4 w-2 h-2 rounded-full"
                      style={{ backgroundColor: annotationColor }}
                      title={`${annotationsBySlide[idx].length} annotations`}
                    />
                  )}
                </div>
                <div className={`absolute top-1 left-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
                  idx === currentIdx ? 'bg-rose-500 text-white' : 'bg-black/40 text-white'
                }`}>{idx + 1}</div>
              </button>
            ))}
          </div>

          {/* Center: slide viewer + playback controls + notes panel */}
          <div className="flex-1 flex flex-col min-w-0 bg-slate-900 relative">

            {/* Keyboard shortcuts overlay */}
            {showShortcuts && (
              <KeyboardShortcutsPanel onClose={() => setShowShortcuts(false)} accentColor={infoType.color} />
            )}

            {/* Slide viewer */}
            <div className="flex-1 relative flex items-center justify-center p-6 min-h-0">
              <div className="relative w-full max-h-full" style={{ aspectRatio: '16/9' }}>
                <img
                  key={transitionKey}
                  src={current.thumbnail}
                  alt={current.label}
                  className="w-full h-full object-cover rounded-xl"
                  style={{ maxHeight: '100%', ...slideAnimStyle }}
                />
                {/* ── Annotation canvas overlay ── */}
                <SlideAnnotationCanvas
                  shapes={annotationsBySlide[currentIdx] ?? []}
                  onShapesChange={shapes =>
                    setAnnotationsBySlide(prev => ({ ...prev, [currentIdx]: shapes }))
                  }
                  tool={annotationTool}
                  color={annotationColor}
                  lineWidth={annotationLineWidth}
                  active={showAnnotations}
                  className="rounded-xl"
                />
                {/* Annotation mode badge */}
                {showAnnotations && (
                  <div
                    className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white animate-pulse"
                    style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(8px)', border: `1px solid ${infoType.color}60` }}
                  >
                    <i className="ri-edit-2-line text-xs" style={{ color: infoType.color }}></i>
                    Annotation mode
                  </div>
                )}
                {/* Slide number + label badges */}
                <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-xs font-bold" style={{ pointerEvents: 'none' }}>
                  {currentIdx + 1} / {slides.length}
                </div>
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20" style={{ pointerEvents: 'none' }}>
                  <span className="text-white text-xs font-medium">{current.label}</span>
                </div>
                {/* Annotation count indicator on slide */}
                {(annotationsBySlide[currentIdx]?.length ?? 0) > 0 && !showAnnotations && (
                  <div
                    className="absolute bottom-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white cursor-pointer"
                    style={{ backgroundColor: `${infoType.color}cc`, backdropFilter: 'blur(6px)' }}
                    onClick={() => setShowAnnotations(true)}
                    title="Click to edit annotations"
                  >
                    <i className="ri-edit-2-line text-[10px]"></i>
                    {annotationsBySlide[currentIdx].length} annotations
                  </div>
                )}
                {/* Hints */}
                {!showAnnotations && (
                  <div className="absolute bottom-3 left-3 flex items-center gap-2 opacity-40 hover:opacity-70 transition-opacity" style={{ pointerEvents: 'none' }}>
                    <kbd className="px-1.5 py-0.5 bg-black/50 text-white text-[9px] font-mono rounded border border-white/20">?</kbd>
                    <span className="text-white/60 text-[9px]">shortcuts</span>
                    <span className="text-white/30 text-[9px]">·</span>
                    <kbd className="px-1.5 py-0.5 bg-black/50 text-white text-[9px] font-mono rounded border border-white/20">A</kbd>
                    <span className="text-white/60 text-[9px]">annotate</span>
                  </div>
                )}
              </div>

              {/* ── Annotation toolbar — floats above slide ── */}
              {showAnnotations && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
                  <AnnotationToolbar
                    tool={annotationTool}
                    onToolChange={setAnnotationTool}
                    color={annotationColor}
                    onColorChange={setAnnotationColor}
                    lineWidth={annotationLineWidth}
                    onLineWidthChange={setAnnotationLineWidth}
                    shapeCount={(annotationsBySlide[currentIdx] ?? []).length}
                    onUndo={undoAnnotation}
                    onClear={clearAnnotations}
                    onClose={() => setShowAnnotations(false)}
                    accentColor={infoType.color}
                  />
                </div>
              )}

              <button onClick={prev} disabled={currentIdx === 0} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 disabled:opacity-0 disabled:pointer-events-none border border-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer" style={{ zIndex: 5 }}>
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <button onClick={next} disabled={currentIdx === slides.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 disabled:opacity-0 disabled:pointer-events-none border border-white/20 rounded-full flex items-center justify-center text-white transition-all cursor-pointer" style={{ zIndex: 5 }}>
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>

            {/* Playback controls */}
            <div className="flex-shrink-0 px-4 py-2.5 flex items-center gap-2 border-t border-white/10 bg-slate-800/50">
              <div className="flex items-center gap-1">
                <button onClick={() => goTo(0)} className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors">
                  <i className="ri-skip-back-line text-sm"></i>
                </button>
                <button onClick={prev} disabled={currentIdx === 0} className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white disabled:opacity-30 cursor-pointer transition-colors">
                  <i className="ri-skip-left-line text-sm"></i>
                </button>
                <button
                  onClick={() => setIsPlaying(p => !p)}
                  className="w-9 h-9 flex items-center justify-center bg-rose-500 hover:bg-rose-400 text-white rounded-full cursor-pointer transition-colors mx-1"
                >
                  <i className={`text-base ${isPlaying ? 'ri-pause-line' : 'ri-play-line'}`}></i>
                </button>
                <button onClick={next} disabled={currentIdx === slides.length - 1} className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors">
                  <i className="ri-skip-right-line text-sm"></i>
                </button>
                <button onClick={() => goTo(slides.length - 1)} className="w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer transition-colors">
                  <i className="ri-skip-forward-line text-sm"></i>
                </button>
              </div>
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-rose-500 to-fuchsia-500 rounded-full transition-all duration-300" style={{ width: `${((currentIdx + 1) / slides.length) * 100}%` }} />
              </div>
              <span className="text-white/50 text-xs font-mono whitespace-nowrap">{currentIdx + 1} / {slides.length}</span>

              {/* ── Transition type picker ── */}
              <div className="hidden md:flex items-center gap-0.5 px-1 py-0.5 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                {(['none', 'fade', 'slide', 'zoom'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTransitionType(t)}
                    title={`Transition: ${t === 'none' ? 'Cut (instant)' : t}`}
                    className="px-2 py-0.5 rounded-md text-[10px] font-bold cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      backgroundColor: transitionType === t ? infoType.color : 'transparent',
                      color: transitionType === t ? 'white' : 'rgba(255,255,255,0.35)',
                    }}
                  >
                    {t === 'none' ? 'Cut' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              {/* Annotate toggle */}
              <button
                onClick={() => setShowAnnotations(a => !a)}
                title="Toggle annotations (A)"
                className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  showAnnotations
                    ? 'text-white'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/10'
                }`}
                style={showAnnotations ? { backgroundColor: infoType.color } : {}}
              >
                <i className="ri-edit-2-line text-xs"></i>
                {showAnnotations ? 'Drawing' : 'Annotate'}
              </button>

              {/* Notes toggle button in controls bar */}
              <button
                onClick={() => setShowNotes(n => !n)}
                className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  showNotes
                    ? 'bg-white/20 text-white'
                    : 'text-white/40 hover:text-white/70 hover:bg-white/10'
                }`}
              >
                <i className="ri-file-text-line text-xs"></i>Notes
                <kbd className="px-1 py-0.5 bg-white/10 text-[9px] font-mono rounded border border-white/20">N</kbd>
              </button>
            </div>

            {/* ── Speaker Notes Panel — animated slide-up ── */}
            <div
              className="overflow-hidden transition-all duration-300 ease-out flex-shrink-0"
              style={{ maxHeight: showNotes ? '172px' : '0px', opacity: showNotes ? 1 : 0 }}
            >
              <SpeakerNotesPanel
                currentSlide={current}
                currentIdx={currentIdx}
                totalSlides={slides.length}
                note={speakerNotes[currentIdx] ?? ''}
                onNoteChange={val => updateNote(currentIdx, val)}
                onClose={() => setShowNotes(false)}
                accentColor={infoType.color}
              />
            </div>

          </div>

          {/* ── Right panel ── */}
          <div className="w-[248px] flex-shrink-0 flex flex-col border-l" style={{ borderColor: 'rgba(255,255,255,0.07)', backgroundColor: '#0d1526' }}>

            {/* Infographic type hero */}
            <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-slate-100" style={{ background: `linear-gradient(135deg, ${infoType.bg} 0%, white 100%)` }}>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Infographic Type</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: infoType.color }}>
                  <i className={`${infoType.icon} text-white text-lg`}></i>
                </div>
                <div>
                  <p className="text-sm font-extrabold text-slate-900 leading-tight">{infoType.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Template format</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1.5 mt-3">
                {[
                  { icon: 'ri-download-line',   val: `${(stats.uses / 1000).toFixed(1)}k`,    label: 'Uses' },
                  { icon: 'ri-star-line',        val: stats.rating,                             label: 'Rating' },
                  { icon: 'ri-file-export-line', val: `${(stats.exports / 1000).toFixed(1)}k`, label: 'Exports' },
                ].map(s => (
                  <div key={s.label} className="text-center p-1.5 bg-white/70 rounded-lg border border-white">
                    <i className={`${s.icon} text-xs`} style={{ color: infoType.color }}></i>
                    <p className="text-xs font-extrabold text-slate-900 mt-0.5">{s.val}</p>
                    <p className="text-[9px] text-slate-400">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Current slide info */}
            <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex-shrink-0">
              <p className="text-xs font-semibold text-slate-500 mb-2">Current Slide</p>
              <div className="flex items-center gap-2 p-2.5 rounded-xl" style={{ backgroundColor: infoType.bg }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: infoType.color }}>
                  <i className={`${current.icon} text-white text-sm`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-900 truncate">{current.label}</p>
                  <p className="text-[10px] text-slate-500 leading-tight truncate">{current.desc}</p>
                </div>
              </div>
            </div>

            {/* Slide list */}
            <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex-shrink-0">
              <p className="text-xs font-semibold text-slate-500 mb-2">All Slides ({slides.length})</p>
              <div className="space-y-0.5">
                {slides.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-all cursor-pointer ${
                      i === currentIdx ? 'text-white' : 'hover:bg-slate-50 text-slate-600'
                    }`}
                    style={i === currentIdx ? { backgroundColor: infoType.color } : {}}
                  >
                    <span className={`text-xs font-mono w-4 flex-shrink-0 ${i === currentIdx ? 'text-white/70' : 'text-slate-400'}`}>{i + 1}</span>
                    <i className={`${s.icon} text-xs flex-shrink-0 ${i === currentIdx ? 'text-white' : ''}`}></i>
                    <span className="text-xs truncate flex-1">{s.label}</span>
                    {/* dot if slide has notes */}
                    {speakerNotes[i] && (
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: i === currentIdx ? 'rgba(255,255,255,0.6)' : infoType.color }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="px-4 pt-3 pb-3 border-b border-slate-100 flex-shrink-0">
              <div className="mb-2.5">
                <p className="text-xs font-semibold text-slate-700 mb-1.5">Color palette</p>
                <div className="flex gap-1">
                  {template.colorPalette.map((c, i) => (
                    <div key={i} className="flex-1 h-5 rounded-md border border-white/50" style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {template.tags.map(t => (
                  <span key={t} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{t}</span>
                ))}
              </div>
              <button
                onClick={onUse}
                className="w-full py-2.5 text-white text-sm font-bold rounded-xl hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                style={{ background: `linear-gradient(135deg, ${infoType.color}, #f43f5e)` }}
              >
                <i className="ri-magic-line mr-1.5"></i>Use This Template
              </button>
              <DownloadSampleButton template={template} accentColor={infoType.color} />
            </div>

            {/* Used by Similar Brands */}
            <UsedBySimilarBrands category={template.category} accentColor={infoType.color} />

          </div>

        </div>

        {/* ── Similar Templates row ── */}
        <SimilarTemplatesRow similar={similar} onSwitch={onSwitchTemplate} accentColor={infoType.color} />

        {/* ── Export Notes Modal ── */}
        {showExportNotes && (
          <ExportNotesModal
            template={template}
            slides={slides}
            speakerNotes={speakerNotes}
            onClose={() => setShowExportNotes(false)}
            accentColor={infoType.color}
          />
        )}
      </div>
    </div>
  );
}
